/**
 * The unified approval inbox — "everything waiting on me", across every module that approves
 * through `@hrms/workflow`.
 *
 * WHY A NEW CONTRACT RATHER THAN A WIDER `/leave/approvals`. Each module already lists its own
 * pending approvals (leave, expense, FnF, attendance…), and each of those lists is correct for its
 * own module. None of them can answer "what is waiting on me" without the caller visiting seven
 * screens. The engine can: a `workflow_steps` row whose `approver_person_id` is the caller and whose
 * `decision` is null IS a pending turn (see `pendingFor` in engine.ts), whatever module owns it —
 * including a step handed to the caller by an out-of-office delegation, since delegation rewrites
 * `approver_person_id` when the step activates and records the original approver in
 * `delegated_from`.
 *
 * DECIDING FROM HERE IS OPT-IN PER ENTITY TYPE. `@hrms/workflow`'s `decide()` only moves the
 * workflow. The module's own side effects — mirroring status onto the record, crediting a leave
 * balance back, recalculating attendance, telling the requester — live in each module's decide
 * path, not in an outbox consumer (see the "WHY NO workflow.* ROUTE" note in
 * apps/worker/src/outbox-relay.ts). So `decideStep` is offered only for entity types whose inbox
 * decision calls the very same function the module's own approve/reject route calls; every other
 * item carries `decideInInbox: false` and a way to open the record, and the client sends the
 * person there to decide.
 *
 * NO SALARY FIGURES, NO PII. `title` is the kind of request ("Leave request", "Salary change"),
 * never the card blurb a starter froze onto the instance — a comp-revision card holds a salary and
 * an expense card can hold an amount. The requester's display name is the only personal detail, and
 * it is one the approver already sees on the record itself.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { aiNavigationSchema } from './ai.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * The inbox's tabs. A module groups entity types the way the menu does (D3: organised by module),
 * so "Attendance" holds regularisations, shift changes and on-duty requests together. `other` is
 * the honest home for an entity type the API has no label for yet — it still appears, it is just
 * not grouped.
 */
export const INBOX_MODULES = [
  'leave',
  'expense',
  'attendance',
  'timesheet',
  'performance',
  'payroll',
  'separation',
  'recruitment',
  'asset',
  'other',
] as const;
export const inboxModuleSchema = z.enum(INBOX_MODULES);
export type InboxModule = z.infer<typeof inboxModuleSchema>;

export const inboxItemSchema = z.object({
  /** The caller's pending turn. The id `decideStep` takes. */
  stepId: uuidSchema,
  instanceId: uuidSchema,
  /** Position in the chain — a second-level approval reads differently from a first. */
  stepNo: z.number().int(),
  /** The workflow entity type (`leave_request`, `expense_claim`, `payroll_run`, …). */
  entityType: z.string(),
  /** The record under approval, in whichever table `entityType` lives in. */
  entityId: uuidSchema,
  module: inboxModuleSchema,
  /** What kind of request this is. Never carries an amount, a reason or other free text. */
  title: z.string().max(120),
  /** Who filed it. */
  requesterPersonId: uuidSchema,
  requesterName: z.string(),
  /**
   * Who it is about, when someone filed it on their behalf (HR raising an exit). Null when the
   * requester filed it about themselves.
   */
  subjectName: z.string().nullable(),
  /** When the request entered approval. */
  submittedAt: instantSchema,
  /** The step's SLA deadline, when its definition sets `slaHours`. */
  dueAt: instantSchema.nullable(),
  isOverdue: z.boolean(),
  /** Set when the step reached the caller by escalation rather than by the chain itself. */
  escalatedAt: instantSchema.nullable(),
  /** Set when the caller is holding this turn for someone else (delegation). */
  delegatedFromPersonId: uuidSchema.nullable(),
  delegatedFromName: z.string().nullable(),
  /**
   * The screen that shows the record, in the same closed target list the copilot uses — each app
   * maps a target to its own route. Null when no target fits this entity type yet; the client can
   * still route on `entityType` + `entityId`.
   */
  navigation: aiNavigationSchema.nullable(),
  /**
   * True when `decideStep` may decide this item — its inbox decision runs the module's own decide
   * path, side effects included. False means "decide on the record page": the module's decision
   * does something the inbox cannot yet reproduce exactly.
   */
  decideInInbox: z.boolean(),
});
export type InboxItem = z.infer<typeof inboxItemSchema>;

export const listInboxQuerySchema = z.object({
  module: inboxModuleSchema.optional(),
  entityType: z.string().min(1).max(64).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListInboxQuery = z.infer<typeof listInboxQuerySchema>;

export const inboxCountsSchema = z.object({
  /** Every pending turn the caller holds, ignoring the `module`/`entityType` filter. */
  all: z.number().int(),
  overdue: z.number().int(),
  /** Per module, ignoring the filter — the numbers a tab strip shows. Absent modules are zero. */
  byModule: z.record(inboxModuleSchema, z.number().int()),
  byEntityType: z.record(z.string(), z.number().int()),
});
export type InboxCounts = z.infer<typeof inboxCountsSchema>;

export const inboxListSchema = z.object({
  items: z.array(inboxItemSchema),
  /** Items matching the filter — what `page`/`limit` paginate over. */
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  counts: inboxCountsSchema,
});
export type InboxList = z.infer<typeof inboxListSchema>;

export const decideInboxStepBodySchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  /** Same bound the module decide routes accept, so an inbox comment is never truncated there. */
  comment: z.string().trim().max(500).optional(),
});
export type DecideInboxStepBody = z.infer<typeof decideInboxStepBodySchema>;

export const decideInboxStepResultSchema = z.object({
  stepId: uuidSchema,
  entityType: z.string(),
  entityId: uuidSchema,
  /**
   * Where the whole request stands after this decision: `pending` when the chain moves on to a
   * later approver, `approved`/`rejected` when this decision settled it.
   */
  workflowStatus: z.enum(['pending', 'approved', 'rejected']),
});
export type DecideInboxStepResult = z.infer<typeof decideInboxStepResultSchema>;

export const inboxContract = c.router(
  {
    /**
     * Signed-in only, deliberately not `workflow:read`: an assigned approver often holds no
     * approving role at all (a `specific_person` step, a cost-centre owner who is a plain employee,
     * a delegate), and assignment is itself the authorisation — the same reasoning `decide()` gives.
     * The query is self-scoped: it only ever returns the caller's own turns.
     */
    listInbox: {
      method: 'GET',
      path: '/',
      query: listInboxQuerySchema,
      responses: {
        200: inboxListSchema,
        401: errorBodySchema,
      },
      summary:
        'Every pending approval waiting on the caller, across modules, with per-module counts',
      metadata: routeMeta(null),
    },

    /**
     * Decide one of the caller's own pending turns. Requires `Idempotency-Key`, like every module
     * decide route. 412 when the item is not `decideInInbox` (decide it on the record page), when
     * it is no longer awaiting a decision, or when it is not the caller's turn to take.
     */
    decideStep: {
      method: 'POST',
      path: '/steps/:stepId/decide',
      pathParams: z.object({ stepId: uuidSchema }),
      body: decideInboxStepBodySchema,
      responses: {
        200: decideInboxStepResultSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: "Approve or reject one of the caller's pending turns from the unified inbox",
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/inbox' },
);
